type IconName =
  | 'search'
  | 'home'
  | 'inbox'
  | 'chart'
  | 'people'
  | 'calendar'
  | 'board'
  | 'list'
  | 'timeline'
  | 'chevron'
  | 'plus'
  | 'close'
  | 'dot'
  | 'flag'
  | 'clock'
  | 'link'
  | 'comment'
  | 'sparkle'
  | 'ticket'
  | 'folder'
  | 'file'
  | 'upload'
  | 'download'
  | 'trash'
  | 'pencil'
  | 'bell'

/**
 * A small hand-drawn icon set.
 *
 * Twenty icons is not worth a dependency, and inlining them means they inherit
 * `currentColor` and the surrounding font size without a wrapper — which is what
 * makes a dense interface line up.
 *
 * Every path is drawn on a 24-unit grid with a 1.75 stroke, so a row of them
 * reads as one family rather than as clip art.
 */
const PATHS: Record<IconName, string> = {
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14ZM20 20l-4-4',
  home: 'M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1Z',
  inbox: 'M4 13h4l1.5 3h5L16 13h4M4 13 6.5 5h11L20 13v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Z',
  chart: 'M4 20V9M10 20V4M16 20v-7M22 20H2',
  people: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM2.5 20a6.5 6.5 0 0 1 13 0M17 11.5a3 3 0 1 0 0-6M18 20h3.5a5.5 5.5 0 0 0-3-4.9',
  calendar: 'M4 8h16M7 3v3M17 3v3M5 20h14a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1Z',
  board: 'M4 5h4v14H4zM10 5h4v9h-4zM16 5h4v11h-4z',
  list: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  timeline: 'M4 7h9M4 12h14M4 17h6M20 5v14',
  chevron: 'm9 6 6 6-6 6',
  plus: 'M12 5v14M5 12h14',
  close: 'm6 6 12 12M18 6 6 18',
  dot: 'M12 12h.01',
  flag: 'M5 21V4M5 5h11l-2 3.5L16 12H5',
  clock: 'M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM12 8v4l2.5 2',
  link: 'M10 13a4 4 0 0 0 5.7.4l2.6-2.6a4 4 0 0 0-5.7-5.7L11.5 6.4M14 11a4 4 0 0 0-5.7-.4L5.7 13.2a4 4 0 0 0 5.7 5.7l1.1-1',
  comment: 'M20 15a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2Z',
  sparkle: 'M12 3.5 13.8 9l5.7 1.8-5.7 1.8L12 18.5l-1.8-5.9L4.5 10.8 10.2 9ZM19 4v3M20.5 5.5h-3',
  ticket: 'M4 9V7a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2a2 2 0 0 0 0 6v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-2a2 2 0 0 0 0-6ZM13 6v12',
  folder: 'M4 19V6a1 1 0 0 1 1-1h4.5l2 2.5H19a1 1 0 0 1 1 1V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Z',
  file: 'M13 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8ZM13 3v5h5',
  upload: 'M12 16V4M8 8l4-4 4 4M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3',
  download: 'M12 4v12M8 12l4 4 4-4M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3',
  trash: 'M4 7h16M10 4h4M9 7v11M15 7v11M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13',
  pencil: 'M4 20h4L20 8l-4-4L4 16ZM14 6l4 4',
  bell: 'M18 8a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6M13.7 20a2 2 0 0 1-3.4 0',
}

export function Icon({
  name,
  className = 'size-4',
  filled = false,
}: {
  name: IconName
  className?: string
  filled?: boolean
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${className}`}
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  )
}

export type { IconName }
